import {
  CheckCircleOutlined,
  ClockCircleFilled,
  ExclamationCircleOutlined,
  FieldTimeOutlined,
  InfoCircleFilled,
  MinusCircleOutlined,
  MinusOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Empty, List, Space, Tag, Typography } from 'antd';
import { message } from '@/components/Message';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { CardSkuInfoPopover } from '.';
import {
  Card,
  CardType,
  CardUser,
  CardUserStatus,
  getCardTypeDesc,
  getCardUserStatusDesc,
  requestActiveCardUser,
  requestCardUserLog,
} from '@/models/card';
import { DateFormat } from '@/models/common';
import { requestUserBasicInfo } from '@/models/user';
import { assertUnreachable } from '@/utils/common';
import AsyncWrapper from '../AsyncWrapper';
import Modal from '../Modal';
import PosButton from '../PosButton';
import UserAvatar from '../User/UserAvatar';
import styles from './cardUser.less';

export const getCardUserStatusDescTag = (card: CardUser): React.ReactNode => {
  switch (card.status) {
    case CardUserStatus.CARD_USER_STATUS_INACTIVE:
      return (
        <Tag color={'blue'} icon={<ExclamationCircleOutlined />}>
          {getCardUserStatusDesc(card)}
        </Tag>
      );
    case CardUserStatus.CARD_USER_STATUS_ACTIVE:
      return (
        <Tag color={'green'} icon={<CheckCircleOutlined />}>
          {getCardUserStatusDesc(card)}
        </Tag>
      );
    case CardUserStatus.CARD_USER_STATUS_FINISH:
    case CardUserStatus.CARD_USER_STATUS_EXPIRED:
    case CardUserStatus.CARD_USER_STATUS_CANCELED:
      return <Tag icon={<MinusCircleOutlined />}>{getCardUserStatusDesc(card)}</Tag>;
  }

  return assertUnreachable(card.status);
};

type Props = {
  card: CardUser;
  onClick?: (card: CardUser) => void;
  showAddTime?: boolean;
  showUserInfo?: boolean;
  selectMode?: boolean;
  /**是否可选择, 可选择: true , 不可选择: 不可选择原因 */
  selectableInfo?: {
    max: number;
    available: boolean;
    /**不可用原因 */
    NAReason?: string;
  };
  onSelect?: (count: number) => void;
  selectCount?: number;
};

const CardUserView: React.FC<Props> = ({
  card: initCard,
  showAddTime = true,
  showUserInfo = false,
  selectMode = false,
  onClick = () => {},
  selectableInfo = { max: 0, available: false },
  onSelect = () => undefined,
  selectCount,
}: Props) => {
  const { xs } = useBreakpoint();
  const [card, setCard] = useState(initCard);
  const [detailMode, setDetailMode] = useState<'log'>(null);
  const { send: getUserBasicInfo, payload: userInfo } = requestUserBasicInfo();
  const { send: activeCardUser } = requestActiveCardUser();
  const {
    send: sendGetCardUserLog,
    payload: cardUserLogPayload,
    loading: cardUserLogLoading,
    error: cardUserLogError,
  } = requestCardUserLog();

  useEffect(() => {
    setCard(initCard);
  }, [initCard]);

  useEffect(() => {
    if (showUserInfo && card && card.userId)
      getUserBasicInfo({
        pathVars: { userId: card.userId },
      }).then();
  }, [showUserInfo, card]);

  const getCardUserLog = (page = 1) => {
    sendGetCardUserLog({
      params: {
        cardUserId: card.id,
        page,
        limit: 3,
      },
    }).then();
  };

  useEffect(() => {
    // 自动选择
    if (selectableInfo?.available) {
      onSelect(selectableInfo.max);
    }
  }, [selectableInfo.available, selectableInfo.max]);

  // useEffect(() => {
  //   onSelect(selectCount);
  // }, [selectCount]);

  return (
    <div className={`${styles.wrapper} ${xs ? styles.xs : ''}`}>
      <div
        className={`${styles.container} ${styles[`type-${card.cardType}`]} ${styles.cardUser}`}
        onClick={() => {
          onClick(card);
        }}>
        <div className={styles.left}>
          {getCardTypeDesc({ type: card.cardType, durationUnit: card.cardDurationUnit } as Card)}
        </div>
        <div className={styles.right}>
          <div className={styles.main}>
            <div className={styles.heading}>
              {selectMode ? null : getCardUserStatusDescTag(card)}{' '}
              <span>
                {card.cardType === CardType.TIMES && card.times !== undefined ? (
                  <>
                    <Tag icon={<InfoCircleFilled />} color={'#2db7f5'} style={{ margin: 0 }}>
                      剩{card.times}次
                    </Tag>{' '}
                  </>
                ) : null}
                {card.expiredTime !== undefined ? (
                  <>
                    <Tag icon={<ClockCircleFilled />} color={'#2db7f5'} style={{ margin: 0 }}>
                      {dayjs(card.expiredTime).format(DateFormat)}
                    </Tag>
                  </>
                ) : null}
              </span>
            </div>

            <CardSkuInfoPopover card={card}>
              <Typography.Title className={styles.name} ellipsis={{ rows: 1 }}>
                {card.cardName}
              </Typography.Title>
            </CardSkuInfoPopover>

            {selectableInfo && selectableInfo.NAReason ? (
              <Tag icon={<ExclamationCircleOutlined />} color="warning">
                {selectableInfo.NAReason}
              </Tag>
            ) : null}
            {!selectMode && (
              <>
                <Typography.Paragraph className={styles.intro} ellipsis={{ rows: 2 }}>
                  {card.cardIntro}
                </Typography.Paragraph>
                {card.expiredTime && card.times === CardType.TIMES && (
                  <div>
                    <FieldTimeOutlined /> 到期时间: {card.expiredTime}
                  </div>
                )}
                {showAddTime && (
                  <Typography.Text type={'secondary'}>
                    <FieldTimeOutlined /> 购卡时间: {card.addTime}
                  </Typography.Text>
                )}
              </>
            )}
          </div>
          <div className={styles.operations}>
            <div className={styles.items}>
              <Space direction={xs ? 'horizontal' : 'vertical'}>
                {selectMode && card.status === CardUserStatus.CARD_USER_STATUS_ACTIVE ? (
                  // 选择模式下(pos结算) 只有可用状态的卡可操作
                  (<Space>
                    <PosButton
                      disabled={!selectableInfo.available || selectCount === 0}
                      icon={<MinusOutlined />}
                      onClick={() => {
                        onSelect(Math.max(0, selectCount - 1));
                      }}
                    />
                    {selectCount}
                    <PosButton
                      disabled={!selectableInfo.available || selectCount === selectableInfo.max}
                      icon={<PlusOutlined />}
                      onClick={() => {
                        onSelect(Math.min(selectCount + 1, selectableInfo.max));
                      }}
                    />
                  </Space>)
                ) : null}
                {!selectMode && (
                  <>
                    {/* <PosButton
                        type={'primary'}
                        onClick={() => {
                          Modal.confirm({
                            title: '确认使用一次该卡?',
                            content:
                              card.cardType === CardType.TIMES ? '使用后该卡剩余次数将减少' : '',
                            onOk: () =>
                              new Promise<void>((resolve, reject) => {
                                writeOffCardUser({
                                  params: { writeOffTimes: 1 },
                                  data: card,
                                })
                                  .then(newCard => {
                                    message.success(`使用成功`);
                                    setCard(Object.assign({}, card, newCard));
                                    setDetailMode(null);
                                    resolve();
                                  })
                                  .catch(e => {
                                    message.error(`使用失败: ${e.msg}`);
                                    reject();
                                  });
                              }),
                          });
                        }}>
                        使用一次
                      </PosButton>
                      <PosButton
                        onClick={() => {
                          modalPrompt({
                            title: '请输入使用次数',
                            useNumberKeyboard: true,
                            numberKeyboardProps: {
                              precision: 0,
                              showAllClean: true,
                            },
                            onOk: times =>
                              new Promise((resolve, reject) => {
                                times = Math.floor(+times);
                                if (!isNaN(times) && times > 0) {
                                  writeOffCardUser({
                                    params: { writeOffTimes: times },
                                    data: card,
                                  })
                                    .then(newCard => {
                                      message.success(`使用成功`);
                                      setCard(Object.assign({}, card, newCard));
                                      setDetailMode(null);
                                      resolve();
                                    })
                                    .catch(e => {
                                      message.error(`使用失败: ${e.msg}`);
                                      reject();
                                    });
                                } else {
                                  message.error('次数请输入大于0的正整数');
                                  reject();
                                }
                              }),
                          });
                        }}>
                        使用多次
                      </PosButton> */}
                    <PosButton
                      onClick={() => {
                        if (detailMode === 'log') {
                          setDetailMode(null);
                        } else {
                          setDetailMode('log');
                          getCardUserLog(1);
                        }
                      }}>
                      {detailMode === 'log' ? <>收起记录</> : <>使用记录</>}
                    </PosButton>
                    <CardSkuInfoPopover card={card}>
                      <PosButton onClick={() => {}}>适用范围</PosButton>
                    </CardSkuInfoPopover>
                  </>
                )}
              </Space>
              {card.status === CardUserStatus.CARD_USER_STATUS_INACTIVE && (
                <>
                  <PosButton
                    type={'primary'}
                    onClick={() => {
                      Modal.confirm({
                        title: '确认激活该卡?',
                        onOk: () =>
                          new Promise<void>((resolve, reject) => {
                            activeCardUser({
                              data: card,
                            })
                              .then(newCard => {
                                message.success(`激活成功`);
                                setCard(Object.assign({}, card, newCard));
                                resolve();
                              })
                              .catch(e => {
                                message.error(`激活失败: ${e.msg}`);
                                reject();
                              });
                          }),
                      });
                    }}>
                    激活
                  </PosButton>
                </>
              )}
            </div>
          </div>
          {userInfo && (
            <div className={styles.user}>
              <UserAvatar user={userInfo} avatarProps={{ size: 24 }} />
            </div>
          )}
        </div>
      </div>
      {detailMode === 'log' && (
        <div className={styles.log}>
          <AsyncWrapper
            error={cardUserLogError}
            loading={cardUserLogLoading}
            render={!!cardUserLogPayload}>
            {() => {
              const logList = cardUserLogPayload.list;
              const page = cardUserLogPayload.page;
              if (!logList || !logList.length) {
                return <Empty description={'该卡无使用记录'} />;
              }
              return (
                <List
                  loading={cardUserLogLoading}
                  itemLayout="horizontal"
                  size={'small'}
                  loadMore={
                    <div className={'align-flex-horizontal-center'} style={{ paddingTop: 15 }}>
                      <PosButton
                        disabled={page.current === 1}
                        loading={cardUserLogLoading}
                        style={{ marginRight: 10 }}
                        onClick={() => {
                          getCardUserLog(page.current - 1);
                        }}>
                        上一页
                      </PosButton>
                      <PosButton
                        disabled={page.current === page.pages}
                        loading={cardUserLogLoading}
                        style={{ marginLeft: 10 }}
                        onClick={() => {
                          getCardUserLog(page.current + 1);
                        }}>
                        下一页
                      </PosButton>
                    </div>
                  }
                  dataSource={logList}
                  renderItem={log => (
                    <List.Item>
                      <List.Item.Meta
                        title={
                          <Space size={20}>
                            <Typography.Text>{log.addTime}</Typography.Text>
                            <Typography.Text type={'secondary'} style={{ fontWeight: 'normal' }}>
                              使用<Typography.Text>{log.times}</Typography.Text>次
                            </Typography.Text>
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              );
            }}
          </AsyncWrapper>
        </div>
      )}
    </div>
  );
};

export default CardUserView;
